import { Injectable } from '@angular/core'
import { CombinedGraphQLErrors } from '@apollo/client/errors'

import {
  Observable,
  ReplaySubject,
  catchError,
  defer,
  map,
  share,
  throwError,
} from 'rxjs'

import {
  CompleteKpiFeedbackSheetResponseGQL,
  ChangeKpiFeedbackSheetAssessmentsGQL,
  CreateKpiFeedbackSheetGQL,
  CreateKpiKnowledgeGQL,
  DeleteKpiEmployeeNumberGQL,
  DeleteKpiFeedbackSheetGQL,
  DeleteKpiKnowledgeGQL,
  DeleteKpiMonthlyDataGQL,
  DeleteKpiMonthlyOrgTargetGQL,
  SaveKpiMonthlyOrgTargetGQL,
  GetKpiMonthlyOrgTargetsGQL,
  GetKpiMonthlyOrgTargetsQuery,
  GetKpiDashboardGQL,
  GetKpiDashboardMembersGQL,
  GetKpiDashboardMembersQuery,
  GetKpiDashboardQuery,
  GetKpiDashboardsGQL,
  GetKpiDashboardsQuery,
  GetKpiEmployeeNumberImportTemplateGQL,
  GetKpiEmployeeNumberMappingsGQL,
  GetKpiEmployeeNumberMappingsQuery,
  GetKpiFeedbackChoicesGQL,
  GetKpiFeedbackChoicesQuery,
  GetKpiFeedbackSheetCandidatesGQL,
  GetKpiFeedbackSheetCandidatesQuery,
  GetKpiFeedbackSheetGQL,
  GetKpiFeedbackSheetQuery,
  GetKpiFeedbackSheetsGQL,
  GetKpiFeedbackSheetsQuery,
  GetKpiImportTemplateGQL,
  GetKpiKnowledgesGQL,
  GetKpiKnowledgesQuery,
  GetKpiMonthlyDataSummariesGQL,
  GetKpiMonthlyDataSummariesQuery,
  GetKpiOperatorSettingsGQL,
  GetKpiOperatorSettingsQuery,
  GetKpiOperatorKeyActionDailyTrendGQL,
  GetKpiOperatorKeyActionDailyTrendQuery,
  GetKpiOperatorSummaryGQL,
  GetKpiOperatorSummaryQuery,
  ImportKpiEmployeeNumbersGQL,
  ImportKpiEmployeeNumbersMutation,
  ImportKpiMonthlyRecordsGQL,
  ImportKpiMonthlyRecordsMutation,
  ImportKpiMonthlyTargetsGQL,
  KpiImportKind,
  TagMatchMode,
  KpiKnowledgeInput,
  KpiPreviousNextActionResultInput,
  SaveKpiOperatorKeyActionsGQL,
  SetKpiCoachingExclusionGQL,
  SetKpiFeedbackNextActionClosedGQL,
  UpdateKpiFeedbackSheetAssessmentGQL,
  UpdateKpiFeedbackSheetAssessmentInput,
  UpdateKpiFeedbackSheetGQL,
  UpdateKpiFeedbackSheetInput,
  UpdateKpiKnowledgeGQL,
  UpdateKpiPreviousNextActionResultGQL,
} from '../../../_generated/graphql'
import { assertGraphqlData } from '../../util/assertGraphqlData'
import {
  ALL_CUSTOM_ERROR_CODES,
  throwIfCustomError,
} from '../../util/throwIfCustomError'

export const KPI_DASHBOARD_NOT_FOUND_MESSAGE =
  'KPI ダッシュボードが見つかりません'
/** ダッシュボードから対象メンバーが除外された場合の業務エラー。 */
export const KPI_DASHBOARD_MEMBER_NOT_FOUND_MESSAGE =
  'KPI ダッシュボードメンバーが見つかりません'
export const KPI_SHEET_ASSESSMENT_NOT_FOUND_MESSAGE =
  'KPI フィードバックシートのアセスメントが見つかりません'
export const KPI_NEXT_ACTION_NOT_FOUND_MESSAGE =
  'ネクストアクションが見つかりません'
export const KPI_SHEET_NOT_FOUND_MESSAGE =
  'KPI フィードバックシートが見つかりません'
export const KPI_ASSESSMENT_ALREADY_USED_MESSAGE =
  '対象アセスメントは既存の active シートですでに使用されています'
/** 最新以外のシートの対象変更を禁止する業務エラー。 */
export const KPI_LATEST_SHEET_ONLY_MESSAGE =
  '対象アセスメントを変更できるのは最新のフィードバックシートだけです'
/** AI 草案作成中の対象変更を禁止する業務エラー。 */
export const KPI_AI_DRAFT_PENDING_MESSAGE =
  'AI 草案作成中は対象アセスメントを変更できません'
/** 現在と同じ対象への変更を禁止する業務エラー。 */
export const KPI_ASSESSMENTS_UNCHANGED_MESSAGE =
  '現在と同じ対象アセスメントには変更できません'
export const KPI_PREVIOUS_SHEET_MESSAGES = {
  sheetMissing:
    '前回シートが存在しないためネクストアクション結果を保存できません',
  actionIdMissing: '前回シートに存在しない nextActionId が指定されています',
  actionMissing: '前回シートのネクストアクションが見つかりません',
} as const

/** 対象アセスメントの変更などで保存先のフィードバックブロックが見つからない場合のエラー。 */
export class KpiFeedbackTargetNotFoundError extends Error {}

export type KpiDashboardDefinition = GetKpiDashboardQuery['kpiDashboard']
export type KpiDashboardItem = KpiDashboardDefinition['items'][number]
export type KpiDashboardKeyAction = KpiDashboardDefinition['keyActions'][number]
export type KpiDashboardMembers =
  GetKpiDashboardMembersQuery['kpiDashboardMembers']
export type KpiDashboardMember = KpiDashboardMembers['members'][number]
export type KpiDashboardAggregate = KpiDashboardMembers['all']
export type KpiMonthlyOrgTargets =
  GetKpiMonthlyOrgTargetsQuery['kpiMonthlyOrgTargets']
export type KpiMonthlyOrgTarget = KpiMonthlyOrgTargets[number]
export type KpiOrgTargetMetric = KpiDashboardAggregate['orgTargets'][number]
export type KpiMemberMetric = KpiDashboardMember['metrics'][number]
export type KpiDashboardSummary = GetKpiDashboardsQuery['kpiDashboards'][number]
export type KpiEmployeeNumberMappings =
  GetKpiEmployeeNumberMappingsQuery['kpiEmployeeNumberMappings']
export type KpiEmployeeNumberMappingView = KpiEmployeeNumberMappings[number]
export type KpiEmployeeNumberImportResultView =
  ImportKpiEmployeeNumbersMutation['importKpiEmployeeNumbers']
export type KpiImportResultView =
  ImportKpiMonthlyRecordsMutation['importKpiMonthlyRecords']
export type KpiMonthlyDataSummaries =
  GetKpiMonthlyDataSummariesQuery['kpiMonthlyDataSummaries']
export type KpiMonthlyDataSummaryView = KpiMonthlyDataSummaries[number]
export type KpiOperatorSummary =
  GetKpiOperatorSummaryQuery['kpiOperatorSummary']
export type KpiOperatorKeyActionDailyTrendResult =
  GetKpiOperatorKeyActionDailyTrendQuery['kpiOperatorKeyActionDailyTrend']
export type KpiOperatorKeyActionTrendWindow =
  KpiOperatorSummary['keyActionTrendWindow']
export type KpiOperatorKeyActionMonthlyTrend =
  KpiOperatorSummary['keyActionMonthlyTrend'][number]
export type KpiOperatorMetric = KpiOperatorSummary['metrics'][number]
export type KpiOperatorMonthlyMetrics =
  KpiOperatorSummary['monthlyTrend'][number]
export type KpiOperatorKeyActionScore =
  KpiOperatorSummary['keyActionScores'][number]
export type KpiOperatorSettings =
  GetKpiOperatorSettingsQuery['kpiOperatorSettings']
export type KpiOperatorSettingRow = KpiOperatorSettings['rows'][number]
export type KpiKnowledges = GetKpiKnowledgesQuery['kpiKnowledges']
export type KpiKnowledgeView = KpiKnowledges[number]
export type KpiFeedbackChoices =
  GetKpiFeedbackChoicesQuery['kpiFeedbackChoices']
export type KpiFeedbackChoiceView = KpiFeedbackChoices[number]
export type KpiFeedbackSheets = GetKpiFeedbackSheetsQuery['kpiFeedbackSheets']
export type KpiFeedbackSheetSummaryView = KpiFeedbackSheets[number]
/** 日時変換済みの前回アクションを持つシート詳細。 */
export type KpiFeedbackSheetView = Omit<
  GetKpiFeedbackSheetQuery['kpiFeedbackSheet'],
  'previousSheet'
> & {
  previousSheet?:
    | (Omit<
        NonNullable<
          GetKpiFeedbackSheetQuery['kpiFeedbackSheet']['previousSheet']
        >,
        'nextActions'
      > & {
        nextActions: KpiPreviousNextActionView[]
      })
    | null
}
/** サーバーの nullable クローズ日時を Date に変換した前回アクション。 */
export type KpiPreviousNextActionView = Omit<
  NonNullable<
    GetKpiFeedbackSheetQuery['kpiFeedbackSheet']['previousSheet']
  >['nextActions'][number],
  'closedAt'
> & { closedAt: Date | null }
export type KpiFeedbackSheetAssessmentView =
  KpiFeedbackSheetView['assessments'][number]
/** サーバーの nullable クローズ日時を Date に変換したアクション。 */
export type KpiNextActionView = Omit<
  KpiFeedbackSheetAssessmentView['nextActions'][number],
  'closedAt'
> & { closedAt: Date | null }
export type KpiFeedbackSheetCandidates =
  GetKpiFeedbackSheetCandidatesQuery['kpiFeedbackSheetCandidates']
export type KpiFeedbackSheetCandidateView = KpiFeedbackSheetCandidates[number]
export type {
  KpiKnowledgeInput,
  KpiPreviousNextActionResultInput,
  UpdateKpiFeedbackSheetAssessmentInput,
  UpdateKpiFeedbackSheetInput,
}

/** GraphQL の必須 Date scalar を Date に変換する。欠落・不正値は throw する。 */
function mapRequiredDate(
  value: Date | null | undefined,
  fieldName: string,
): Date {
  if (value == null) {
    throw new Error(`GraphQL response field is missing: ${fieldName}`)
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    throw new Error(`GraphQL response field is an invalid date: ${fieldName}`)
  }
  return date
}

/** GraphQL の nullable Date scalar を null を保ったまま Date に変換する。 */
function mapNullableDate(
  value: Date | null | undefined,
  fieldName: string,
): Date | null {
  if (typeof value === 'undefined') {
    throw new Error(`GraphQL response field is missing: ${fieldName}`)
  }
  return value == null ? null : mapRequiredDate(value, fieldName)
}

/** KPI ナレッジの Date scalar を Date に変換する。 */
function mapKpiKnowledgeDates(knowledge: KpiKnowledgeView): KpiKnowledgeView {
  return {
    ...knowledge,
    updatedAt: mapRequiredDate(knowledge.updatedAt, 'kpiKnowledge.updatedAt'),
  }
}

/** フィードバックシート一覧項目の Date scalar を Date に変換する。 */
function mapKpiFeedbackSheetSummaryDates(
  sheet: KpiFeedbackSheetSummaryView,
): KpiFeedbackSheetSummaryView {
  return {
    ...sheet,
    createdAt: mapRequiredDate(sheet.createdAt, 'kpiFeedbackSheets.createdAt'),
  }
}

/** 参照ナレッジなどの詳細を保持し、シートと配下アセスメントの Date scalar を変換する。 */
function mapKpiFeedbackSheetDates(
  sheet: GetKpiFeedbackSheetQuery['kpiFeedbackSheet'],
): KpiFeedbackSheetView {
  return {
    ...sheet,
    interviewDate: mapNullableDate(
      sheet.interviewDate,
      'kpiFeedbackSheet.interviewDate',
    ),
    createdAt: mapRequiredDate(sheet.createdAt, 'kpiFeedbackSheet.createdAt'),
    updatedAt: mapRequiredDate(sheet.updatedAt, 'kpiFeedbackSheet.updatedAt'),
    assessments: sheet.assessments.map((assessment) => ({
      ...assessment,
      nextActions: assessment.nextActions.map(mapKpiNextActionDates),
      assessDate: mapRequiredDate(
        assessment.assessDate,
        'kpiFeedbackSheet.assessments.assessDate',
      ),
    })),
    previousSheet:
      sheet.previousSheet == null
        ? sheet.previousSheet
        : {
            ...sheet.previousSheet,
            nextActions: sheet.previousSheet.nextActions.map(
              mapKpiNextActionDates,
            ),
          },
  }
}

/** アクションの nullable クローズ日時を Date に変換する。 */
function mapKpiNextActionDates<T extends { closedAt?: Date | null }>(
  action: T,
): Omit<T, 'closedAt'> & { closedAt: Date | null } {
  return {
    ...action,
    closedAt: mapNullableDate(action.closedAt, 'kpiNextAction.closedAt'),
  }
}

/** フィードバックシート候補の Date scalar を Date に変換する。 */
function mapKpiFeedbackSheetCandidateDates(
  candidate: KpiFeedbackSheetCandidateView,
): KpiFeedbackSheetCandidateView {
  return {
    ...candidate,
    assessDate: mapRequiredDate(
      candidate.assessDate,
      'kpiFeedbackSheetCandidates.assessDate',
    ),
  }
}

@Injectable({
  providedIn: 'root',
})
export class KpiDashboardService {
  private readonly dashboards$: Observable<KpiDashboardSummary[]>
  private readonly feedbackChoices$: Observable<KpiFeedbackChoices>

  constructor(
    private getKpiDashboardGQL: GetKpiDashboardGQL,
    private getKpiDashboardsGQL: GetKpiDashboardsGQL,
    private getKpiEmployeeNumberImportTemplateGQL: GetKpiEmployeeNumberImportTemplateGQL,
    private getKpiEmployeeNumberMappingsGQL: GetKpiEmployeeNumberMappingsGQL,
    private importKpiEmployeeNumbersGQL: ImportKpiEmployeeNumbersGQL,
    private deleteKpiEmployeeNumberGQL: DeleteKpiEmployeeNumberGQL,
    private getKpiImportTemplateGQL: GetKpiImportTemplateGQL,
    private getKpiMonthlyDataSummariesGQL: GetKpiMonthlyDataSummariesGQL,
    private importKpiMonthlyRecordsGQL: ImportKpiMonthlyRecordsGQL,
    private importKpiMonthlyTargetsGQL: ImportKpiMonthlyTargetsGQL,
    private deleteKpiMonthlyDataGQL: DeleteKpiMonthlyDataGQL,
    private getKpiDashboardMembersGQL: GetKpiDashboardMembersGQL,
    private setKpiCoachingExclusionGQL: SetKpiCoachingExclusionGQL,
    private getKpiOperatorSummaryGQL: GetKpiOperatorSummaryGQL,
    private getKpiOperatorKeyActionDailyTrendGQL: GetKpiOperatorKeyActionDailyTrendGQL,
    private getKpiOperatorSettingsGQL: GetKpiOperatorSettingsGQL,
    private saveKpiOperatorKeyActionsGQL: SaveKpiOperatorKeyActionsGQL,
    private getKpiKnowledgesGQL: GetKpiKnowledgesGQL,
    private createKpiKnowledgeGQL: CreateKpiKnowledgeGQL,
    private updateKpiKnowledgeGQL: UpdateKpiKnowledgeGQL,
    private deleteKpiKnowledgeGQL: DeleteKpiKnowledgeGQL,
    private getKpiFeedbackChoicesGQL: GetKpiFeedbackChoicesGQL,
    private getKpiFeedbackSheetsGQL: GetKpiFeedbackSheetsGQL,
    private getKpiFeedbackSheetGQL: GetKpiFeedbackSheetGQL,
    private getKpiFeedbackSheetCandidatesGQL: GetKpiFeedbackSheetCandidatesGQL,
    private createKpiFeedbackSheetGQL: CreateKpiFeedbackSheetGQL,
    private updateKpiFeedbackSheetGQL: UpdateKpiFeedbackSheetGQL,
    private updateKpiFeedbackSheetAssessmentGQL: UpdateKpiFeedbackSheetAssessmentGQL,
    private updateKpiPreviousNextActionResultGQL: UpdateKpiPreviousNextActionResultGQL,
    private completeKpiFeedbackSheetResponseGQL: CompleteKpiFeedbackSheetResponseGQL,
    private deleteKpiFeedbackSheetGQL: DeleteKpiFeedbackSheetGQL,
    private setKpiFeedbackNextActionClosedGQL: SetKpiFeedbackNextActionClosedGQL,
    private changeKpiFeedbackSheetAssessmentsGQL: ChangeKpiFeedbackSheetAssessmentsGQL,
    private getKpiMonthlyOrgTargetsGQL: GetKpiMonthlyOrgTargetsGQL,
    private saveKpiMonthlyOrgTargetGQL: SaveKpiMonthlyOrgTargetGQL,
    private deleteKpiMonthlyOrgTargetGQL: DeleteKpiMonthlyOrgTargetGQL,
  ) {
    this.dashboards$ = defer(() =>
      this.getKpiDashboardsGQL.fetch({ fetchPolicy: 'no-cache' }),
    ).pipe(
      map((response) => {
        assertGraphqlData(response)
        return response.data.kpiDashboards
      }),
      catchError((error: unknown) => {
        throwIfCustomError(error)
        return throwError(() => error)
      }),
      // ナビ・ガード・一覧の 3 箇所で共有するセッションキャッシュ（設計 §5.1）。
      // メンバー変更の反映はフルリロードで足りる想定。resetOnError でエラーは
      // キャッシュせず、次の購読で再フェッチして復旧できるようにする
      // （header の retry はこの reset 前提で再購読している）
      share({
        connector: () => new ReplaySubject<KpiDashboardSummary[]>(1),
        resetOnError: true,
        resetOnComplete: false,
        resetOnRefCountZero: false,
      }),
    )
    this.feedbackChoices$ = defer(() =>
      this.getKpiFeedbackChoicesGQL.fetch({ fetchPolicy: 'no-cache' }),
    ).pipe(
      map((response) => {
        assertGraphqlData(response)
        return response.data.kpiFeedbackChoices
      }),
      catchError((error: unknown) => {
        throwIfCustomError(error)
        return throwError(() => error)
      }),
      share({
        connector: () => new ReplaySubject<KpiFeedbackChoices>(1),
        resetOnError: true,
        resetOnComplete: false,
        resetOnRefCountZero: false,
      }),
    )
  }

  dashboards(): Observable<KpiDashboardSummary[]> {
    return this.dashboards$
  }

  getDashboard(dashboardId: string): Observable<KpiDashboardDefinition> {
    return this.getKpiDashboardGQL
      .fetch({
        variables: { dashboardId },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.kpiDashboard
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error, ALL_CUSTOM_ERROR_CODES)
          return throwError(() => error)
        }),
      )
  }

  /** active ユーザーと、対応行が残る inactive ユーザーの対照一覧を取得する。 */
  employeeNumberMappings(
    dashboardId: string,
  ): Observable<KpiEmployeeNumberMappings> {
    return this.getKpiEmployeeNumberMappingsGQL
      .fetch({
        variables: { dashboardId },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.kpiEmployeeNumberMappings
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  /** backend の社員番号ヘッダー定義から CSV テンプレートを取得する。 */
  employeeNumberImportTemplate(dashboardId: string): Observable<string> {
    return this.getKpiEmployeeNumberImportTemplateGQL
      .fetch({
        variables: { dashboardId },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.kpiEmployeeNumberImportTemplate
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  /** 社員番号ファイルを検証または取り込む。 */
  importEmployeeNumbers(
    dashboardId: string,
    fileBase64: string,
    dryRun: boolean,
  ): Observable<KpiEmployeeNumberImportResultView> {
    return this.importKpiEmployeeNumbersGQL
      .mutate({
        variables: { dashboardId, fileBase64, dryRun },
      })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.importKpiEmployeeNumbers
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  /** 指定ユーザーの社員番号を削除する。 */
  deleteEmployeeNumber(
    dashboardId: string,
    userId: string,
  ): Observable<boolean> {
    return this.deleteKpiEmployeeNumberGQL
      .mutate({ variables: { dashboardId, userId } })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.deleteKpiEmployeeNumber
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  members(
    dashboardId: string,
    periodFrom: string,
    periodTo: string,
    scoreCount: number,
  ): Observable<KpiDashboardMembers> {
    return this.getKpiDashboardMembersGQL
      .fetch({
        variables: { dashboardId, periodFrom, periodTo, scoreCount },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.kpiDashboardMembers
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error, ALL_CUSTOM_ERROR_CODES)
          return throwError(() => error)
        }),
      )
  }

  /** 指定オペレーターの KPI 実績・推移・KeyAction 設定を取得する。 */
  operatorSummary(
    dashboardId: string,
    userId: string,
    scoreCount: number,
    tagIds?: string[] | null,
    tagMatch?: TagMatchMode | null,
  ): Observable<KpiOperatorSummary> {
    return this.getKpiOperatorSummaryGQL
      .fetch({
        variables: { dashboardId, userId, scoreCount, tagIds, tagMatch },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.kpiOperatorSummary
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error, ALL_CUSTOM_ERROR_CODES)
          return throwError(() => error)
        }),
      )
  }

  /** 指定月の重点強化 KeyAction スコアの日次推移を取得する。 */
  operatorKeyActionDailyTrend(
    dashboardId: string,
    userId: string,
    period: string,
  ): Observable<KpiOperatorKeyActionDailyTrendResult> {
    return this.getKpiOperatorKeyActionDailyTrendGQL
      .fetch({
        variables: { dashboardId, userId, period },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.kpiOperatorKeyActionDailyTrend
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error, ALL_CUSTOM_ERROR_CODES)
          return throwError(() => error)
        }),
      )
  }

  /** KPI オペレーター設定一覧を取得する。 */
  operatorSettings(dashboardId: string): Observable<KpiOperatorSettings> {
    return this.getKpiOperatorSettingsGQL
      .fetch({
        variables: { dashboardId },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.kpiOperatorSettings
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  /** 指定オペレーターの重点強化 KeyAction を保存する。 */
  saveOperatorKeyActions(
    dashboardId: string,
    userId: string,
    keyActionIds: string[],
  ): Observable<boolean> {
    return this.saveKpiOperatorKeyActionsGQL
      .mutate({
        variables: { dashboardId, userId, keyActionIds },
      })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.saveKpiOperatorKeyActions
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  /** AI 草案生成で参照するナレッジ一覧を取得する。 */
  knowledges(dashboardId: string): Observable<KpiKnowledges> {
    return this.getKpiKnowledgesGQL
      .fetch({
        variables: { dashboardId },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.kpiKnowledges.map(mapKpiKnowledgeDates)
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  /** AI 草案生成で参照するナレッジを作成する。 */
  createKnowledge(
    dashboardId: string,
    input: KpiKnowledgeInput,
  ): Observable<KpiKnowledgeView> {
    return this.createKpiKnowledgeGQL
      .mutate({ variables: { dashboardId, input } })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return mapKpiKnowledgeDates(response.data.createKpiKnowledge)
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  /** AI 草案生成で参照するナレッジを更新する。 */
  updateKnowledge(
    knowledgeId: string,
    input: KpiKnowledgeInput,
  ): Observable<KpiKnowledgeView> {
    return this.updateKpiKnowledgeGQL
      .mutate({ variables: { knowledgeId, input } })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return mapKpiKnowledgeDates(response.data.updateKpiKnowledge)
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  /** AI 草案生成で参照するナレッジを論理削除する。 */
  deleteKnowledge(knowledgeId: string): Observable<boolean> {
    return this.deleteKpiKnowledgeGQL
      .mutate({ variables: { knowledgeId } })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.deleteKpiKnowledge
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  /** フィードバックシートの回答選択肢を取得する。 */
  feedbackChoices(): Observable<KpiFeedbackChoices> {
    return this.feedbackChoices$
  }

  /** 指定オペレーターのフィードバックシート一覧を取得する。 */
  feedbackSheets(
    dashboardId: string,
    userId: string,
  ): Observable<KpiFeedbackSheets> {
    return this.getKpiFeedbackSheetsGQL
      .fetch({
        variables: { dashboardId, userId },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.kpiFeedbackSheets.map(
            mapKpiFeedbackSheetSummaryDates,
          )
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error, ALL_CUSTOM_ERROR_CODES)
          return throwError(() => error)
        }),
      )
  }

  /** フィードバックシートの詳細を取得する。 */
  feedbackSheet(sheetId: string): Observable<KpiFeedbackSheetView> {
    return this.getKpiFeedbackSheetGQL
      .fetch({
        variables: { sheetId },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return mapKpiFeedbackSheetDates(response.data.kpiFeedbackSheet)
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error, ALL_CUSTOM_ERROR_CODES)
          return throwError(() => error)
        }),
      )
  }

  /** 新規シートで選択可能なアセスメント候補を取得する。 */
  feedbackSheetCandidates(
    dashboardId: string,
    userId: string,
    sheetId?: string,
  ): Observable<KpiFeedbackSheetCandidates> {
    return this.getKpiFeedbackSheetCandidatesGQL
      .fetch({
        variables: {
          dashboardId,
          userId,
          ...(sheetId == null ? {} : { sheetId }),
        },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.kpiFeedbackSheetCandidates.map(
            mapKpiFeedbackSheetCandidateDates,
          )
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error, ALL_CUSTOM_ERROR_CODES)
          return throwError(() => error)
        }),
      )
  }

  /** 選択したアセスメントからフィードバックシートを作成する。 */
  createFeedbackSheet(
    dashboardId: string,
    userId: string,
    assessmentIds: string[],
  ): Observable<string> {
    return this.createKpiFeedbackSheetGQL
      .mutate({ variables: { dashboardId, userId, assessmentIds } })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.createKpiFeedbackSheet
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error, ALL_CUSTOM_ERROR_CODES)
          return throwError(() => error)
        }),
      )
  }

  /** 最新シートの対象アセスメントを変更し、変更したシート ID を返す。 */
  changeFeedbackSheetAssessments(
    sheetId: string,
    assessmentIds: string[],
  ): Observable<string> {
    return this.changeKpiFeedbackSheetAssessmentsGQL
      .mutate({ variables: { sheetId, assessmentIds } })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.changeKpiFeedbackSheetAssessments
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error, ALL_CUSTOM_ERROR_CODES)
          return throwError(() => error)
        }),
      )
  }

  /** フィードバックシート本体の指定項目を更新する。 */
  updateFeedbackSheet(
    sheetId: string,
    input: UpdateKpiFeedbackSheetInput,
  ): Observable<boolean> {
    return this.updateKpiFeedbackSheetGQL
      .mutate({ variables: { sheetId, input } })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.updateKpiFeedbackSheet
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error, ALL_CUSTOM_ERROR_CODES)
          return throwError(() => error)
        }),
      )
  }

  /** 前回ネクストアクションをクローズまたは再開する。 */
  setFeedbackNextActionClosed(
    sheetAssessmentId: string,
    nextActionId: string,
    closed: boolean,
  ): Observable<KpiNextActionView> {
    return this.setKpiFeedbackNextActionClosedGQL
      .mutate({ variables: { sheetAssessmentId, nextActionId, closed } })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return mapKpiNextActionDates(
            response.data.setKpiFeedbackNextActionClosed,
          )
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error, ALL_CUSTOM_ERROR_CODES)
          return throwError(() => error)
        }),
      )
  }

  /** フィードバック対象アセスメントの指定項目を更新する。 */
  updateFeedbackSheetAssessment(
    sheetAssessmentId: string,
    input: UpdateKpiFeedbackSheetAssessmentInput,
  ): Observable<boolean> {
    return this.updateKpiFeedbackSheetAssessmentGQL
      .mutate({ variables: { sheetAssessmentId, input } })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.updateKpiFeedbackSheetAssessment
        }),
        catchError((error: unknown) => {
          if (CombinedGraphQLErrors.is(error)) {
            const notFoundError = error.errors.find(
              (err) =>
                err.extensions?.code === 'NOT_FOUND' &&
                err.message === KPI_SHEET_ASSESSMENT_NOT_FOUND_MESSAGE,
            )
            if (notFoundError != null) {
              throw new KpiFeedbackTargetNotFoundError(notFoundError.message)
            }
          }
          throwIfCustomError(error, ALL_CUSTOM_ERROR_CODES)
          return throwError(() => error)
        }),
      )
  }

  /** 前回ネクストアクションの結果を 1 件保存し、確定したアクションを返す。 */
  updatePreviousNextActionResult(
    sheetId: string,
    input: KpiPreviousNextActionResultInput,
  ): Observable<KpiPreviousNextActionView> {
    return this.updateKpiPreviousNextActionResultGQL
      .mutate({ variables: { sheetId, input } })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          const action = response.data.updateKpiPreviousNextActionResult
          return mapKpiNextActionDates(action)
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error, ALL_CUSTOM_ERROR_CODES)
          return throwError(() => error)
        }),
      )
  }

  /** 本人の記入完了を記録する。 */
  completeFeedbackSheetResponse(sheetId: string): Observable<boolean> {
    return this.completeKpiFeedbackSheetResponseGQL
      .mutate({ variables: { sheetId } })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.completeKpiFeedbackSheetResponse
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error, ALL_CUSTOM_ERROR_CODES)
          return throwError(() => error)
        }),
      )
  }

  /** フィードバックシートを論理削除する。 */
  deleteFeedbackSheet(sheetId: string): Observable<boolean> {
    return this.deleteKpiFeedbackSheetGQL
      .mutate({ variables: { sheetId } })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.deleteKpiFeedbackSheet
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error, ALL_CUSTOM_ERROR_CODES)
          return throwError(() => error)
        }),
      )
  }

  setCoachingExclusion(
    dashboardId: string,
    userId: string,
    excluded: boolean,
  ): Observable<boolean> {
    return this.setKpiCoachingExclusionGQL
      .mutate({
        variables: { dashboardId, userId, excluded },
      })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.setKpiCoachingExclusion
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  getImportTemplate(
    dashboardId: string,
    kind: KpiImportKind,
  ): Observable<string> {
    return this.getKpiImportTemplateGQL
      .fetch({
        variables: { dashboardId, kind },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.kpiImportTemplate
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  /** 取込済み月次データの年月別サマリーを取得する。 */
  getMonthlyDataSummaries(
    dashboardId: string,
    kind: KpiImportKind,
  ): Observable<KpiMonthlyDataSummaries> {
    return this.getKpiMonthlyDataSummariesGQL
      .fetch({
        variables: { dashboardId, kind },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.kpiMonthlyDataSummaries
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  importMonthlyRecords(
    dashboardId: string,
    fileBase64: string,
    period: string,
    dryRun: boolean,
  ): Observable<KpiImportResultView> {
    return this.importKpiMonthlyRecordsGQL
      .mutate({
        variables: { dashboardId, fileBase64, period, dryRun },
      })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.importKpiMonthlyRecords
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  importMonthlyTargets(
    dashboardId: string,
    fileBase64: string,
    period: string,
    dryRun: boolean,
  ): Observable<KpiImportResultView> {
    return this.importKpiMonthlyTargetsGQL
      .mutate({
        variables: { dashboardId, fileBase64, period, dryRun },
      })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.importKpiMonthlyTargets
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  /** 組織目標の設定履歴を年月降順で取得する。 */
  orgTargets(dashboardId: string): Observable<KpiMonthlyOrgTargets> {
    return this.getKpiMonthlyOrgTargetsGQL
      .fetch({ variables: { dashboardId }, fetchPolicy: 'no-cache' })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.kpiMonthlyOrgTargets
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  /** 指定月の組織目標を保存する。空配列はその月以降の解除を表す。 */
  saveOrgTarget(
    dashboardId: string,
    period: string,
    targets: { itemId: string; targetRate: number }[],
  ): Observable<KpiMonthlyOrgTarget> {
    return this.saveKpiMonthlyOrgTargetGQL
      .mutate({ variables: { dashboardId, period, targets } })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.saveKpiMonthlyOrgTarget
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  /** 指定月の設定行を削除し、直前の設定を引き継ぐ状態に戻す。 */
  deleteOrgTarget(dashboardId: string, period: string): Observable<boolean> {
    return this.deleteKpiMonthlyOrgTargetGQL
      .mutate({ variables: { dashboardId, period } })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.deleteKpiMonthlyOrgTarget
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  deleteMonthlyData(
    dashboardId: string,
    period: string,
    kind: KpiImportKind,
  ): Observable<number> {
    return this.deleteKpiMonthlyDataGQL
      .mutate({
        variables: { dashboardId, period, kind },
      })
      .pipe(
        map((response) => {
          assertGraphqlData(response)
          return response.data.deleteKpiMonthlyData
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }
}
